import React from 'react';
import { Upload, Button, message, Popconfirm } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { FuelReceipt } from '../../types';
import { fuelReceiptApi } from '../../services/api';

interface FuelReceiptManualUploadProps {
  t: (key: string) => string;
  isMobile: boolean;
  uploadKey: number;
  receiptImage: File | null;
  editingReceipt: FuelReceipt | null;
  onImageChange: (file: File | null) => void;
  onEditingReceiptUpdate: (receipt: FuelReceipt) => void;
  onFetchReceipts: () => void;
  validateFile: (file: File) => boolean;
}

const FuelReceiptManualUpload: React.FC<FuelReceiptManualUploadProps> = ({
  t,
  isMobile,
  uploadKey,
  receiptImage,
  editingReceipt,
  onImageChange,
  onEditingReceiptUpdate,
  onFetchReceipts,
  validateFile
}) => {
  const handleViewFile = async () => {
    if (!editingReceipt) return;
    
    const token = localStorage.getItem('token');
    const url = fuelReceiptApi.getReceiptImage(editingReceipt.id);
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) {
      message.error(t('failed.to.load.image') || 'Failed to load file');
      return;
    }
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    window.open(blobUrl, '_blank', 'noopener,noreferrer');
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
  };

  const handleDownloadFile = () => {
    if (!editingReceipt) return;
    
    const link = document.createElement('a');
    link.href = fuelReceiptApi.downloadReceiptImage(editingReceipt.id);
    link.download = `fuel_receipt_${editingReceipt.id}.${editingReceipt.receiptImageContentType?.split('/')[1] || 'file'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteFile = async () => {
    if (!editingReceipt) return;
    
    try {
      await fuelReceiptApi.deleteReceiptImage(editingReceipt.id);
      message.success(t('file.deleted.successfully') || 'File deleted successfully');
      const updatedReceipt = await fuelReceiptApi.getById(editingReceipt.id);
      onEditingReceiptUpdate(updatedReceipt.data);
      onFetchReceipts();
    } catch (error: any) {
      message.error(error?.message || t('failed.to.delete.file') || 'Failed to delete file');
    }
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <h4 style={{ fontSize: isMobile ? '14px' : '16px' }}>
        {t('attach.receipt') || 'Attach Receipt (Optional)'}
      </h4>
      
      {editingReceipt && editingReceipt.hasReceiptImage && (
        <div style={{ marginBottom: 16 }}>
          <p><strong>{t('current.file') || 'Current File'}:</strong></p>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
            <Button 
              onClick={handleViewFile}
              icon={<span>👁️</span>}
              size="small"
            >
              {t('view.current.file') || 'View Current File'}
            </Button>
            <Button 
              onClick={handleDownloadFile}
              icon={<span>⬇️</span>}
              size="small"
            >
              {t('download') || 'Download'}
            </Button>
            <Popconfirm
              title={t('delete.current.file.confirm') || 'Are you sure you want to delete the current file?'}
              onConfirm={handleDeleteFile}
              okText={t('yes')}
              cancelText={t('no')}
            >
              <Button 
                danger
                icon={<span>🗑️</span>}
                size="small"
              >
                {t('delete.file') || 'Delete File'}
              </Button>
            </Popconfirm>
          </div>
          <p style={{ fontSize: '12px', color: '#666', marginTop: 8 }}>
            {t('upload.new.to.replace') || 'Upload a new file below to replace the current file'}
          </p>
        </div>
      )}
      
      <Upload
        key={uploadKey}
        beforeUpload={(file) => {
          if (validateFile(file)) {
            onImageChange(file);
          }
          return false;
        }}
        showUploadList={true}
        accept="image/*,.pdf"
        maxCount={1}
        fileList={receiptImage ? [{
          uid: '-1',
          name: receiptImage.name,
          status: 'done' as const,
        }] : []}
        onRemove={() => {
          onImageChange(null);
        }}
      >
        <Button icon={<UploadOutlined />}>
          {t('upload.receipt.file') || 'Upload Receipt File'}
        </Button>
      </Upload>
    </div>
  );
};

export default FuelReceiptManualUpload;
